import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Paper,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Alert
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    School as SchoolIcon,
    Psychology as PsychologyIcon,
    Science as ScienceIcon,
    CheckCircle as CheckCircleIcon,
    Info as InfoIcon,
    Lightbulb as LightbulbIcon
} from '@mui/icons-material';

const AcademicGuidanceModal = ({ open, onClose }) => {
    const [expandedPanel, setExpandedPanel] = useState('overview');

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpandedPanel(isExpanded ? panel : false);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SchoolIcon sx={{ mr: 2, fontSize: '2rem' }} />
                    Academic Identity Builder Guide
                </Box>
            </DialogTitle>

            <DialogContent>
                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                        This interface is designed for academic researchers to create scientifically rigorous agent identities 
                        using validated psychological scales and systematic methodology.
                    </Typography>
                </Alert>

                <Accordion 
                    expanded={expandedPanel === 'overview'} 
                    onChange={handleAccordionChange('overview')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                            <InfoIcon sx={{ mr: 1 }} />
                            Overview: Academic Approach to Agent Identity
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            The Academic Identity Builder represents a paradigm shift from simple template-based agent creation 
                            to psychometrically grounded identity definition. This approach ensures:
                        </Typography>
                        
                        <List>
                            <ListItem>
                                <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                <ListItemText 
                                    primary="Scientific Rigor" 
                                    secondary="All psychological scales are properly documented with authors, validation studies, and metadata"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                <ListItemText 
                                    primary="Reproducibility" 
                                    secondary="Identity definitions can be exported, shared, and replicated across studies"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                <ListItemText 
                                    primary="Transparency" 
                                    secondary="Complete visibility into how psychological traits are converted to agent behavior"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                <ListItemText 
                                    primary="Validation Ready" 
                                    secondary="Foundation for empirical validation of agent behavior against human responses"
                                />
                            </ListItem>
                        </List>
                    </AccordionDetails>
                </Accordion>

                <Accordion 
                    expanded={expandedPanel === 'operators'} 
                    onChange={handleAccordionChange('operators')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                            <PsychologyIcon sx={{ mr: 1 }} />
                            Understanding Operators (Psychological Scales)
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            Operators are the building blocks of agent identity, representing validated psychological instruments 
                            converted to computational form. They include:
                        </Typography>
                        
                        <Box sx={{ mb: 3 }}>
                            <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    Published Scales
                                </Typography>
                                <Typography variant="body2">
                                    Peer-reviewed psychological instruments from the literature (e.g., Big Five Inventory, 
                                    Beck Depression Inventory). These have established psychometric properties and citations.
                                </Typography>
                            </Paper>
                            
                            <Paper sx={{ p: 2, bgcolor: 'secondary.light', color: 'secondary.contrastText' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    <PsychologyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    User-Created Scales
                                </Typography>
                                <Typography variant="body2">
                                    Custom instruments developed by researchers for specific studies. These require 
                                    documentation of development methodology and validation status.
                                </Typography>
                            </Paper>
                        </Box>
                        
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Key Components of an Operator:
                        </Typography>
                        <List dense>
                            <ListItem>
                                <ListItemText 
                                    primary="Factors" 
                                    secondary="Psychological dimensions measured by the scale (e.g., Extraversion, Neuroticism)"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText 
                                    primary="Items" 
                                    secondary="Individual questions or statements from the scale"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText 
                                    primary="Response Model" 
                                    secondary="How responses are structured (Likert scales, binary, etc.)"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText 
                                    primary="Prompt Templates" 
                                    secondary="How the scale is converted to natural language for the AI agent"
                                />
                            </ListItem>
                        </List>
                    </AccordionDetails>
                </Accordion>

                <Accordion 
                    expanded={expandedPanel === 'workflow'} 
                    onChange={handleAccordionChange('workflow')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                            <ScienceIcon sx={{ mr: 1 }} />
                            Research Workflow
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            Follow this systematic approach for creating academically rigorous agent identities:
                        </Typography>
                        
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Phase 1: Literature Review and Scale Selection
                            </Typography>
                            <List dense>
                                <ListItem sx={{ pl: 4 }}>
                                    <ListItemIcon><Chip label="1" size="small" /></ListItemIcon>
                                    <ListItemText primary="Identify psychological constructs relevant to your research question" />
                                </ListItem>
                                <ListItem sx={{ pl: 4 }}>
                                    <ListItemIcon><Chip label="2" size="small" /></ListItemIcon>
                                    <ListItemText primary="Search the operator library for validated scales" />
                                </ListItem>
                                <ListItem sx={{ pl: 4 }}>
                                    <ListItemIcon><Chip label="3" size="small" /></ListItemIcon>
                                    <ListItemText primary="Review scale metadata, authors, and validation studies" />
                                </ListItem>
                            </List>
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Phase 2: Identity Construction
                            </Typography>
                            <List dense>
                                <ListItem sx={{ pl: 4 }}>
                                    <ListItemIcon><Chip label="4" size="small" /></ListItemIcon>
                                    <ListItemText primary="Drag selected operators into the identity builder" />
                                </ListItem>
                                <ListItem sx={{ pl: 4 }}>
                                    <ListItemIcon><Chip label="5" size="small" /></ListItemIcon>
                                    <ListItemText primary="Configure factor values based on your research design" />
                                </ListItem>
                                <ListItem sx={{ pl: 4 }}>
                                    <ListItemIcon><Chip label="6" size="small" /></ListItemIcon>
                                    <ListItemText primary="Optionally add autobiographic memory for context" />
                                </ListItem>
                            </List>
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Phase 3: Validation and Documentation
                            </Typography>
                            <List dense>
                                <ListItem sx={{ pl: 4 }}>
                                    <ListItemIcon><Chip label="7" size="small" /></ListItemIcon>
                                    <ListItemText primary="Preview the generated prompt to ensure accuracy" />
                                </ListItem>
                                <ListItem sx={{ pl: 4 }}>
                                    <ListItemIcon><Chip label="8" size="small" /></ListItemIcon>
                                    <ListItemText primary="Save with descriptive names for future reference" />
                                </ListItem>
                                <ListItem sx={{ pl: 4 }}>
                                    <ListItemIcon><Chip label="9" size="small" /></ListItemIcon>
                                    <ListItemText primary="Document methodology in your research publications" />
                                </ListItem>
                            </List>
                        </Box>
                    </AccordionDetails>
                </Accordion>

                <Accordion 
                    expanded={expandedPanel === 'best-practices'} 
                    onChange={handleAccordionChange('best-practices')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                            <LightbulbIcon sx={{ mr: 1 }} />
                            Best Practices and Guidelines
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <strong>Important:</strong> Always cite the original scale authors and validation studies 
                                when publishing research using these operators.
                            </Typography>
                        </Alert>
                        
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Scale Selection Guidelines:
                        </Typography>
                        <List dense>
                            <ListItem>
                                <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                                <ListItemText primary="Prefer published scales with established psychometric properties" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                                <ListItemText primary="Consider cultural validity for your target population" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                                <ListItemText primary="Check for recent validation studies and updates" />
                            </ListItem>
                        </List>
                        
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
                            Factor Configuration:
                        </Typography>
                        <List dense>
                            <ListItem>
                                <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                                <ListItemText primary="Use empirically justified factor values when available" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                                <ListItemText primary="Document rationale for chosen values in your methodology" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                                <ListItemText primary="Consider using population norms or clinical cutoffs" />
                            </ListItem>
                        </List>
                        
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
                            Documentation Standards:
                        </Typography>
                        <List dense>
                            <ListItem>
                                <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                                <ListItemText primary="Maintain detailed records of all operator configurations" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                                <ListItemText primary="Version control for any custom operators you create" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                                <ListItemText primary="Share operators with the research community when appropriate" />
                            </ListItem>
                        </List>
                    </AccordionDetails>
                </Accordion>

                <Accordion 
                    expanded={expandedPanel === 'validation'} 
                    onChange={handleAccordionChange('validation')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                            <ScienceIcon sx={{ mr: 1 }} />
                            Future Validation Infrastructure
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" paragraph>
                            The Academic Identity Builder is designed to support future empirical validation of agent behavior:
                        </Typography>
                        
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Planned Validation Features:
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemText 
                                        primary="Behavioral Validation Pipeline" 
                                        secondary="Compare agent responses to human data from the same psychological scales"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText 
                                        primary="Simulation Analytics" 
                                        secondary="Track how different factor configurations affect agent behavior patterns"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText 
                                        primary="Cross-Validation Studies" 
                                        secondary="Systematic comparison of agent performance across different scales and contexts"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText 
                                        primary="Community Benchmarks" 
                                        secondary="Shared validation datasets for comparing operator effectiveness"
                                    />
                                </ListItem>
                            </List>
                        </Box>
                        
                        <Alert severity="info">
                            <Typography variant="body2">
                                By using this academic approach now, you're building a foundation for rigorous 
                                empirical validation of AI agent behavior in future updates to the platform.
                            </Typography>
                        </Alert>
                    </AccordionDetails>
                </Accordion>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    Close Guide
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AcademicGuidanceModal;
